import { createRootMapping, matchMappingAgainstPathname } from './Mapping';
import { RouteObservable } from './RouteObservable';
import { RouteMapObservable } from './RouteMapObservable';
import { Resolver, Status } from './Resolver';
import { RouteType } from './Route';
import { createPromiseFromObservable } from './Observable';
import { createURLDescriptor } from './URLTools';
import { HTTPMethod } from './HTTPMethod';
import { NaviNodeType } from './Node';
import { OutOfRootError } from './Errors'; // The public factory function creates a resolver.

export function createRouter(options) {
  return new Router(new Resolver(), options);
}

var Router =
/** @class */
function () {
  function Router(resolver, options) {
    if (process.env.NODE_ENV !== "production") {
      if (!options.pages || options.pages.type !== NaviNodeType.Switch) {
        // TODO: support top-level context nodes
        throw new Error("Expected to receive a Switch object for the \"pages\" prop, but instead received \"" + options.pages + "\".");
      }
    }

    this.resolver = resolver;
    this.rootContext = options.rootContext || {};
    this.pages = options.pages;
    var basename = options.basename;

    if (basename && basename.slice(-1) === '/') {
      basename = basename.slice(0, -1);
    }

    this.rootMapping = createRootMapping(options.pages, basename);
  }

  Router.prototype.createObservable = function (urlOrDescriptor, options) {
    if (options === void 0) {
      options = {};
    } // need to somehow keep track of which promises in the resolver correspond to which observables,
    // so that I don't end up updating observables which haven't actually changed.


    var url = createURLDescriptor(urlOrDescriptor);
    var rootEnv = {
      context: this.rootContext,
      method: options.method || HTTPMethod.Get,
      params: url.query,
      pathname: '',
      query: url.query,
      search: url.search,
      router: this,
      unmatchedPathnamePart: url.pathname
    };
    var matchEnv = matchMappingAgainstPathname(rootEnv, this.rootMapping, true);

    if (matchEnv) {
      return new RouteObservable(url, matchEnv, this.pages, this.resolver, !!options.withContent);
    }
  };

  Router.prototype.createMapObservable = function (urlOrDescriptor, options) {
    if (options === void 0) {
      options = {};
    }

    return new RouteMapObservable(createURLDescriptor(urlOrDescriptor, {
      ensureTrailingSlash: false
    }), this.rootContext, this.pages, this.rootMapping, this.resolver, this, options);
  };

  Router.prototype.resolve = function (urls, options) {
    var _this = this;

    if (options === void 0) {
      options = {};
    }

    var urlDescriptors = getDescriptorsArray(urls);

    if (!urlDescriptors.length) {
      return Promise.resolve([]);
    }

    var promises = urlDescriptors.map(function (url) {
      return _this.getPageRoutePromise(url, options);
    });
    return !Array.isArray(urls) ? promises[0] : Promise.all(promises);
  };

  Router.prototype.resolveSiteMap = function (urlOrDescriptor, options) {
    if (options === void 0) {
      options = {};
    }

    return createPromiseFromObservable(this.createMapObservable(urlOrDescriptor, options)).then(function (routeMap) {
      var pageMap = {};
      var redirectMap = {};
      var urls = Object.keys(routeMap);

      for (var i = 0; i < urls.length; i++) {
        var url = urls[i];
        var route = routeMap[url];

        if (route.status === Status.Ready) {
          if (route.type === RouteType.Page) {
            pageMap[url] = route;
            continue;
          } else if (route.type === RouteType.Redirect) {
            redirectMap[url] = route;
            continue;
          }
        }

        throw route.error || new Error('router error');
      }

      return {
        pages: pageMap,
        redirects: redirectMap
      };
    });
  };

  Router.prototype.resolvePageMap = function (urlOrDescriptor, options) {
    if (options === void 0) {
      options = {};
    }

    return this.resolveSiteMap(urlOrDescriptor, options).then(function (siteMap) {
      return siteMap.pages;
    });
  };

  Router.prototype.getPageRoutePromise = function (url, options) {
    var _this = this;

    var observable = this.createObservable(url, options);

    if (!observable) {
      return Promise.reject(new OutOfRootError(url));
    }

    return createPromiseFromObservable(observable).then(function (route) {
      if (route.status === Status.Ready) {
        if (route.type === RouteType.Redirect && options.followRedirects) {
          return _this.getPageRoutePromise(createURLDescriptor(route.to), options);
        } else if (route.type === RouteType.Page) {
          return route;
        }
      }

      throw route.error || new Error('router error');
    });
  };

  return Router;
}();

export { Router };

function getDescriptorsArray(urls) {
  return Array.isArray(urls) ? urls.map(function (url) {
    return createURLDescriptor(url);
  }) : [createURLDescriptor(urls)];
}